import { useEffect, useRef } from 'react';
import ImageUploading from 'react-images-uploading';
import { v4 as uuidv4 } from 'uuid';

import useStateHandler from '@/hooks/useStateHandler';
import UserService from '@/services/user.service';

import './BoardBodyPicture.css';

const SUPPORTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

function getFileExtension(file) {
  const fileName = file?.name || '';
  const extension = fileName.split('.').pop();
  return extension && extension !== fileName ? extension.toUpperCase() : 'unknown';
}

export default function ImageManager({
  setTriggerImageUpload,
  dispatch,
}) {
  const onImageUploadRef = useRef(null);
  const { stateHandler } = useStateHandler();

  const showUploadError = text => {
    stateHandler.set('temporalPopup', { text, variant: 'error' });
  };

  async function handlePostImageUpload(file) {
    const { response, data } = await UserService.uploadImage(file);
    if (response.ok) {
      return data;
    } else {
      console.error(`[BoardBodyPicture] Upload ✗ file="${file.name}"`);
      showUploadError(`"${file.name}" could not be uploaded. Please try again.`);
    }
  }

  const onImagesUploaded = async (imageList) => {
    const uploads = imageList.map(({ file }, index) => ({
      file,
      pictureId: index + 1,
      uploadId: uuidv4(),
    }));

    dispatch({
      type: 'BLOCK_CREATED',
      blockType: 'post_pictures',
      data: {
        pictures: uploads.map(({ pictureId, uploadId }) => ({
          pictureId,
          uploadId,
          status: 'uploading',
        })),
      },
    });

    for (const upload of uploads) {
      const dataResponse = await handlePostImageUpload(upload.file);
      if (dataResponse) {
        dispatch({
          type: 'PICTURE_UPLOAD_COMPLETED',
          uploadId: upload.uploadId,
          picture: {
            ...dataResponse,
            pictureId: upload.pictureId,
          },
        });
      } else {
        dispatch({ type: 'PICTURE_UPLOAD_FAILED', uploadId: upload.uploadId });
      }
    }
  };

  useEffect(() => {
    setTriggerImageUpload(() => () => {
      onImageUploadRef.current?.();
    });
  }, [setTriggerImageUpload]);

  return (
    <div className="board-body-picture">
      <ImageUploading
        multiple
        maxNumber={10}
        maxFileSize={15_000_000}
        acceptType={SUPPORTED_IMAGE_EXTENSIONS}
        onChange={onImagesUploaded}
        onError={(errs, files) => {
          console.error('[BoardBodyPicture] onError:', errs);
          if (errs.maxNumber) {
            showUploadError('You can add up to 10 images at once.');
          } else if (errs.acceptType) {
            const extension = getFileExtension(files?.[0]?.file);
            showUploadError(
              `Image format not supported (${extension}). Please use JPEG, PNG, WebP, or GIF.`
            );
          } else if (errs.maxFileSize) {
            showUploadError('Each image must be 15 MB or smaller.');
          }
        }}
      >
        {({ onImageUpload }) => {
          onImageUploadRef.current ||= onImageUpload;
          return null;
        }}
      </ImageUploading>
    </div>
  );
}
